import { useState, useEffect, Suspense, lazy, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { FosAppShell } from './components/layout/flipperos/AppShell';
import { SandboxBanner } from './components/SandboxBanner';
import { ComingSoonBanner } from './components/ComingSoonBanner';
import { AppLoadingScreen } from './components/layout/AppLoadingScreen';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthPage } from '@/pages/auth/AuthPage';
import { Landing } from '@/pages/Landing';
import { AuthPrompt } from '@/components/auth/AuthPrompt';

function LandingWrapper() {
  const openAuth = () => {
    const w = window.open('/auth', '_blank');
    if (!w) window.location.href = '/auth';
  };
  return <Landing onSignIn={openAuth} onStartFree={openAuth} />;
}
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import Security from './pages/legal/Security';
import AboutPage from './pages/About';
import HelpPage from './pages/Help';
import Onboarding from './pages/Onboarding';
// The legacy `FeatureTour` is replaced by the For Visibility TourEngine + welcome
// tour, which auto-fires on first authenticated dashboard visit and lives
// inside FosAppShell. Keep the old import out of the tree to avoid two
// onboarding overlays competing for the screen on first sign-in.
import { AIConsole } from './components/ai/AIConsole';
import { KeyboardShortcutsOverlay } from './components/KeyboardShortcutsOverlay';
import { AuthGateProvider } from '@/context/AuthGateContext';
import { AuthGateModal } from '@/components/auth/AuthGateModal';

// Lazy-loaded page routes for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Inventory = lazy(() => import('./pages/Inventory'));
const PointOfSale = lazy(() => import('./pages/PointOfSale'));
const TransactionHistory = lazy(() => import('./pages/TransactionHistory'));
const Customers = lazy(() => import('./pages/Customers'));
const Auctions = lazy(() => import('./pages/Auctions'));
const Logistics = lazy(() => import('./pages/Logistics'));
const Insurance = lazy(() => import('./pages/Insurance'));
const VisualSearch = lazy(() => import('./pages/VisualSearch'));
const Settings = lazy(() => import('./pages/Settings'));
const AgentActivity = lazy(() => import('./pages/AgentActivity'));
const PublishVariations = lazy(() => import('./pages/PublishVariations'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Automation = lazy(() => import('./pages/Automation'));
const PricingIntel = lazy(() => import('./pages/PricingIntel'));
const Appraisal = lazy(() => import('./pages/Appraisal'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Messages = lazy(() => import('./pages/Messages'));
const Sourcing = lazy(() => import('./pages/Sourcing'));
const SourcingMobile = lazy(() => import('./pages/SourcingMobile'));
const TaxExport = lazy(() => import('./pages/TaxExport'));
const IncomeStatement = lazy(() => import('./pages/IncomeStatement'));
const Offers = lazy(() => import('./pages/Offers'));
const QueuedListings = lazy(() => import('./pages/QueuedListings'));
const Expenses = lazy(() => import('./pages/Expenses'));
const NotFound = lazy(() => import('./pages/NotFound'));
const ConsignorPortal = lazy(() => import('./pages/ConsignorPortal'));
const EbayCallback = lazy(() => import('./pages/auth/EbayCallback'));
const OAuthCallback = lazy(() => import('./pages/auth/OAuthCallback'));
const StatusPage = lazy(() =>
  import('./pages/StatusPage').then((m) => ({ default: m.StatusPage })),
);
const CompareVendoo = lazy(() => import('./pages/compare/CompareVendoo'));
const CompareCloso = lazy(() => import('./pages/compare/CompareCloso'));
const CompareSellerChamp = lazy(() => import('./pages/compare/CompareSellerChamp'));
const BestEbayTools = lazy(() => import('./pages/compare/BestEbayResellerTools2026'));
// Sprint 23: operator-only activation funnel dashboard.
const AdminActivation = lazy(() => import('./pages/admin/Activation'));
const Changelog = lazy(() => import('./pages/Changelog'));
const Roadmap = lazy(() => import('./pages/Roadmap'));
const SourcingAdd = lazy(() => import('./pages/SourcingAdd'));
const Valuation = lazy(() => import('./pages/Valuation'));

function PageSkeleton() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

const App = () => {
  const location = useLocation();
  const { pathname } = location;
  const { isAuthenticated, isLoading, isRestricted, user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  // showTour kept as an explicit "post-onboarding cue" flag, but instead of
  // mounting the legacy FeatureTour we let the new TourEngine's auto-start
  // pick up the welcome tour on next /-route. Setting `showTour` no longer
  // mounts anything directly.
  const showTourRef = useRef(false);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setProfileLoading(false);
      return;
    }

    const checkOnboardingStatus = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          setShowOnboarding(true);
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed_at')
          .eq('id', user.id)
          .single();

        const tourComplete = localStorage.getItem('forvisibility_tour_complete');

        // The shared demo account skips onboarding and the tour. Its address
        // comes from VITE_DEMO_EMAIL; a hardcoded one never matched (fl-aae).
        const demoEmail = (import.meta.env['VITE_DEMO_EMAIL'] as string | undefined) ?? '';
        const isDemo = !!demoEmail && (user.email ?? '').toLowerCase() === demoEmail.toLowerCase();
        if (!profile?.onboarding_completed_at && !isDemo) {
          setShowOnboarding(true);
        } else if (!tourComplete && !isDemo) {
          showTourRef.current = true;
        }
      } catch {
        // Profile check failed — proceed to dashboard
      } finally {
        setProfileLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [isAuthenticated]);

  if (pathname === '/status') {
    return (
      <ErrorBoundary>
        <SandboxBanner />
        <Suspense fallback={<PageSkeleton />}>
          <StatusPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (isLoading || profileLoading) {
    return <AppLoadingScreen />;
  }

  if (isAuthenticated && isRestricted) {
    return (
      <>
        <ComingSoonBanner />
        <div className="flex min-h-screen items-center justify-center bg-fos-bg-base p-8">
          <div className="max-w-md space-y-4 text-center">
            <h1 className="text-2xl font-bold">🚧 Early Access</h1>
            <p className="text-fos-text-muted">
              For Visibility is in pre-launch. Production accounts are limited to early testers.
            </p>
            <p className="text-sm text-fos-text-muted">
              Try the{' '}
              <button
                onClick={async () => {
                  const demoEmail = import.meta.env['VITE_DEMO_EMAIL'] as string;
                  const demoPassword = import.meta.env['VITE_DEMO_PASSWORD'] as string;
                  if (demoEmail && demoPassword) {
                    const { error } = await supabase.auth.signInWithPassword({
                      email: demoEmail,
                      password: demoPassword,
                    });
                    if (!error) window.location.href = '/';
                  }
                }}
                className="underline hover:no-underline"
              >
                demo account
              </button>{' '}
              to explore the platform.
            </p>
            <p className="text-xs text-fos-text-muted">
              Signed in as {user?.email}
              {' · '}
              <button
                onClick={async () => {
                  await supabase.auth.signOut();
                  window.location.href = '/';
                }}
                className="underline hover:no-underline"
              >
                Sign out
              </button>
            </p>
          </div>
        </div>
      </>
    );
  }

  if (showOnboarding) {
    return (
      <Onboarding
        onComplete={() => {
          setShowOnboarding(false);
          showTourRef.current = true;
        }}
      />
    );
  }

  // Routes are split into TWO buckets:
  //   • Public routes (Landing, AuthPage, eBay OAuth callback, public Pricing)
  //     render WITHOUT the FosAppShell so unauthenticated visitors don't see
  //     the dashboard sidebar/header wrapping a login form.
  //   • Authenticated routes render INSIDE FosAppShell.
  // The unauthenticated home (`/`) routes to Landing instead of bouncing
  // straight to /auth — gives first-time visitors marketing context first.
  const publicRoutes = (
    <Routes>
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<OAuthCallback />} />
      <Route path="/auth/ebay/callback" element={<EbayCallback />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/" element={<LandingWrapper />} />
      <Route path="/legal/terms" element={<Terms />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/security" element={<Security />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/compare/vendoo" element={<CompareVendoo />} />
      <Route path="/compare/closo" element={<CompareCloso />} />
      <Route path="/compare/sellerchamp" element={<CompareSellerChamp />} />
      <Route path="/compare/best-ebay-reseller-tools-2026" element={<BestEbayTools />} />
      <Route path="/changelog" element={<Changelog />} />
      <Route path="/roadmap" element={<Roadmap />} />
      <Route path="*" element={<AuthPrompt />} />
    </Routes>
  );

  const authedRoutes = (
    <Routes>
      <Route path="/auth" element={<Navigate to="/" replace />} />
      <Route path="/auth/ebay/callback" element={<EbayCallback />} />
      <Route path="/auth/callback" element={<OAuthCallback />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/" element={<Dashboard />} />
      <Route path="/legal/terms" element={<Terms />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/security" element={<Security />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/compare/vendoo" element={<CompareVendoo />} />
      <Route path="/compare/closo" element={<CompareCloso />} />
      <Route path="/compare/sellerchamp" element={<CompareSellerChamp />} />
      <Route path="/compare/best-ebay-reseller-tools-2026" element={<BestEbayTools />} />
      <Route path="/changelog" element={<Changelog />} />
      <Route path="/roadmap" element={<Roadmap />} />
      <Route path="/inventory" element={<Inventory />} />
      <Route path="/pos" element={<PointOfSale />} />
      <Route path="/transactions" element={<TransactionHistory />} />
      <Route path="/customers" element={<Customers />} />
      <Route path="/expenses" element={<Expenses />} />
      <Route path="/auctions" element={<Auctions />} />
      <Route path="/logistics" element={<Logistics />} />
      <Route path="/insurance" element={<Insurance />} />
      <Route path="/visual-search" element={<VisualSearch />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/settings/*" element={<Settings />} />
      <Route path="/agents/activity" element={<AgentActivity />} />
      <Route path="/listings" element={<Navigate to="/inventory" replace />} />
      <Route path="/listings/queued" element={<QueuedListings />} />
      <Route path="/listings/variations/new" element={<PublishVariations />} />
      <Route path="/offers" element={<Offers />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/automation" element={<Automation />} />
      <Route path="/pricing-intel" element={<PricingIntel />} />
      <Route path="/appraisal" element={<Appraisal />} />
      <Route path="/valuation" element={<Valuation />} />
      <Route path="/messages" element={<Messages />} />
      <Route path="/sourcing" element={<Sourcing />} />
      <Route path="/sourcing/add" element={<SourcingAdd />} />
      <Route path="/sourcing/scan" element={<SourcingMobile />} />
      <Route path="/tax-export" element={<TaxExport />} />
      <Route path="/income-statement" element={<IncomeStatement />} />
      <Route path="/consignor" element={<ConsignorPortal />} />
      <Route path="/admin/activation" element={<AdminActivation />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );

  return (
    <AuthGateProvider>
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => (
          <div className="flex min-h-screen items-center justify-center p-6">
            <div className="w-full max-w-md space-y-4 rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
              <div className="gradient-primary mx-auto flex h-12 w-12 items-center justify-center rounded-xl">
                <span className="text-2xl font-bold text-white">!</span>
              </div>
              <h2 className="text-xl font-semibold text-foreground">Something went wrong</h2>
              <p className="text-sm text-muted-foreground">
                An unexpected error occurred. The team has been notified.
              </p>
              {error ? (
                <details className="rounded-lg bg-muted p-3 text-left text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Error details</summary>
                  <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap text-muted-foreground">
                    {String(error)}
                  </pre>
                </details>
              ) : null}
              <div className="flex justify-center gap-2">
                <button
                  onClick={resetError}
                  className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Try again
                </button>
                <a
                  href="/"
                  className="inline-flex items-center justify-center rounded-lg border px-5 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Reload page
                </a>
              </div>
            </div>
          </div>
        )}
      >
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AIConsole />
          <KeyboardShortcutsOverlay />
          <AuthGateModal />
          <SandboxBanner />
          <ComingSoonBanner />
          {isAuthenticated ? (
            <FosAppShell>
              <ErrorBoundary>
                <Suspense fallback={<PageSkeleton />}>{authedRoutes}</Suspense>
              </ErrorBoundary>
            </FosAppShell>
          ) : (
            <ErrorBoundary>
              <Suspense fallback={<PageSkeleton />}>{publicRoutes}</Suspense>
            </ErrorBoundary>
          )}
        </TooltipProvider>
      </Sentry.ErrorBoundary>
    </AuthGateProvider>
  );
};

export default App;
